import React, { PureComponent } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { List, Record, Map } from "immutable"
import _noop from "lodash/noop"
import { Waypoint } from "react-waypoint"

// actions
import {
  listConfigurationHistory,
  revertConfigurationTo
} from "actions/configurationHistory.action"
import { showToast } from "actions/toast.action"

// selectors
import {
  getConfigurationHistoryList,
  getConfigurationHistorySelectionSettings,
  hasConfigurationHistoryMoreItems,
  isConfigurationHistoryFulfilled
} from "selectors/configurationHistory.selector"

import { getUsersWorkspaceAcl } from "selectors/usersAcl.selector"

// ui components
import Paper from "components/UI/elements/Paper"
import PaperHeader from "components/UI/elements/PaperHeader"
import Button from "components/UI/elements/Button"
import IconButton from "components/UI/elements/IconButton"
import ConfirmModal from "components/UI/components/ConfirmModal"
import DateTimeWithTooltip from "components/UI/elements/DateTimeWithTooltip"

// helpers
import { getRoutePath } from "routes"
import PendingPromise from "helpers/pendingPromise.helper"
import { hasWritePermission } from "helpers/authenticatedUser.helper"
import { goBack } from "helpers/backButton.helper"
import { getConfigurationChangedKeys } from "helpers/objectChanges.helper"
import Username from "helpers/Username.helper"

// constants
import { CONFIGURATION, MODAL, TOAST } from "sharedConstants"

class ConfigurationHistoryList extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      confirmModal: Map({
        open: false,
        historyId: null,
        isLoading: false
      })
    }

    this.pendingPromises = new PendingPromise()
  }

  componentDidMount() {
    const {
      listConfigurationHistory,
      match: {
        params: { id, cid }
      }
    } = this.props
    listConfigurationHistory(id, cid, 0, CONFIGURATION.HISTORY.LOADING_LIMIT, 0).catch(_noop)
  }

  componentWillUnmount() {
    this.pendingPromises.cancelAll()
  }

  loadMoreHistoryItems = () => {
    const {
      listConfigurationHistory,
      selectionSettings,
      match: {
        params: { id, cid }
      }
    } = this.props

    listConfigurationHistory(
      id,
      cid,
      selectionSettings.offset + selectionSettings.limit,
      selectionSettings.limit,
      0
    ).catch(_noop)
  }

  renderWaypoint = () => {
    const { hasMoreHistoryItems } = this.props
    if (hasMoreHistoryItems) {
      return <Waypoint onEnter={this.loadMoreHistoryItems} bottomOffset={-250} />
    }
  }

  confirmConfigurationRevert = () => {
    if (!this.state.confirmModal.get("isLoading")) {
      const {
        revertConfigurationTo,
        showToast,
        match: {
          params: { id, cid }
        }
      } = this.props
      const { confirmModal } = this.state

      this.setState(prevState => ({
        confirmModal: prevState.confirmModal.set("isLoading", true)
      }))

      const revertToPromise = this.pendingPromises.create(
        revertConfigurationTo(id, cid, confirmModal.get("historyId"), 0, true)
      )
      revertToPromise.promise
        .then(() => {
          this.setState(prevState => ({
            confirmModal: prevState.confirmModal.set("open", false)
          }))
          showToast("Configuration has been reverted.", TOAST.TYPE.SUCCESS)
          this.pendingPromises.remove(revertToPromise)
        })
        .catch(() => {
          this.pendingPromises.remove(revertToPromise)
        })
    }
  }

  toggleConfirmModal = (historyId = null) => evt => {
    evt.preventDefault()
    this.setState(prevState => ({
      confirmModal: prevState.confirmModal
        .set("historyId", historyId)
        .set("open", !prevState.confirmModal.get("open"))
        .set("isLoading", false)
    }))
  }

  render() {
    const {
      historyList,
      isHistoryListFulfilled,
      usersAcl,
      history,
      match: {
        params: { id, cid }
      }
    } = this.props
    const { confirmModal } = this.state
    const isEditable = hasWritePermission(usersAcl)

    return (
      <section className="history-list-page">
        {isHistoryListFulfilled && List.isList(historyList) && (
          <React.Fragment>
            <PaperHeader size="small" className="history-list-header">
              <div className="navigation-block">
                <Button
                  className="back-link"
                  onClick={goBack(
                    history,
                    getRoutePath("workspace.configuration.show", { id, cid })
                  )}
                  size="small"
                  color="none"
                >
                  <FontAwesomeIcon icon={["fas", "chevron-left"]} /> Back
                </Button>
                <h3>Configuration Version History</h3>
              </div>
            </PaperHeader>
            <Paper hasHeader={true} className="history-list-content">
              {historyList.size > 0 && (
                <React.Fragment>
                  <div className="table table-content-clickable-row">
                    <div className="thead">
                      <div className="table-row">
                        <div className="table-head hover-overlap-helper">&nbsp;</div>
                        <div className="status table-head user-column">User</div>
                        <div className="table-head modified-column">Modified</div>
                        <div className="table-head">Changed</div>
                        <div className="table-head action-column">&nbsp;</div>
                        <div className="table-head hover-overlap-helper">&nbsp;</div>
                      </div>
                    </div>
                    <div className="tbody">
                      {historyList.map(historyItem => {
                        return (
                          <Link
                            key={historyItem.id ? historyItem.id : 0}
                            className="table-row"
                            to={getRoutePath("workspace.configuration.configurationHistory.show", {
                              id,
                              cid,
                              hid: historyItem.id ? historyItem.id : 0
                            })}
                          >
                            <div className="table-cell hover-overlap-helper">&nbsp;</div>
                            <div className="table-cell author-text">
                              <Username userId={historyItem.getIn(["configuration", "user_id"])} />
                            </div>
                            <div className="table-cell">
                              <DateTimeWithTooltip
                                dateTime={historyItem.getIn(["configuration", "created"])}
                                uniqueTooltipId={`history-item-${historyItem.id}-tooltip`}
                              />
                            </div>
                            <div className="table-cell">
                              {getConfigurationChangedKeys(
                                historyItem.configuration,
                                historyItem.getIn(["previous_history", "configuration"], Map())
                              ).join(", ")}
                            </div>
                            <div className="table-cell action-column">
                              <IconButton
                                color="green"
                                onClick={this.toggleConfirmModal(historyItem.id)}
                                disabled={!isEditable || !historyItem.id}
                              >
                                <FontAwesomeIcon icon={["far", "undo"]} />
                              </IconButton>
                            </div>
                            <div className="table-cell hover-overlap-helper">&nbsp;</div>
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                  {this.renderWaypoint()}
                </React.Fragment>
              )}
            </Paper>
            <ConfirmModal
              open={confirmModal.get("open")}
              type={MODAL.TYPE.SUCCESS}
              handleClose={this.toggleConfirmModal()}
              handleConfirm={this.confirmConfigurationRevert}
              title="Revert configuration"
              text="Do you really want to revert configuration to selected history item?"
              isLoading={confirmModal.get("isLoading")}
            />
          </React.Fragment>
        )}
      </section>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    historyList: getConfigurationHistoryList(state),
    selectionSettings: getConfigurationHistorySelectionSettings(state),
    hasMoreHistoryItems: hasConfigurationHistoryMoreItems(state),
    usersAcl: getUsersWorkspaceAcl(state, ownProps.match.params.id),
    isHistoryListFulfilled: isConfigurationHistoryFulfilled(state)
  }
}

ConfigurationHistoryList.propTypes = {
  historyList: PropTypes.instanceOf(List).isRequired,
  isHistoryListFulfilled: PropTypes.bool.isRequired,
  selectionSettings: PropTypes.instanceOf(Record).isRequired,
  hasMoreHistoryItems: PropTypes.bool.isRequired,
  usersAcl: PropTypes.instanceOf(Record),
  listConfigurationHistory: PropTypes.func.isRequired,
  revertConfigurationTo: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired
}

export default connect(mapStateToProps, {
  listConfigurationHistory,
  revertConfigurationTo,
  showToast
})(ConfigurationHistoryList)
